import { useState, useEffect, useRef, useCallback, createContext, useContext } from "react";
import { createPortal } from "react-dom";
import {
  Paperclip, X, MagnifyingGlassPlus, MagnifyingGlassMinus, ArrowCounterClockwise,
  DownloadSimple, CaretLeft, CaretRight,
} from "@phosphor-icons/react";

const isImage = (ct) => (ct || "").startsWith("image/");

const GalleryContext = createContext(null);

/**
 * Wrap a conversation's message list in this to make all its image attachments
 * browsable as a single gallery (left/right arrows). `images` is the ordered
 * list of image attachments [{id, content_type, original_filename}] across the
 * conversation; `loadBlob(id)` returns a Promise<Blob> (caller owns auth).
 */
export function MessageGalleryProvider({ images, loadBlob, children }) {
  const [startId, setStartId] = useState(null);
  const open = useCallback((attId) => setStartId(attId), []);
  const close = useCallback(() => setStartId(null), []);
  const startIndex = startId ? Math.max(0, images.findIndex((im) => im.id === startId)) : 0;

  return (
    <GalleryContext.Provider value={{ open }}>
      {children}
      {startId && images.length > 0 && (
        <ImageLightbox images={images} startIndex={startIndex} loadBlob={loadBlob} onClose={close} />
      )}
    </GalleryContext.Provider>
  );
}

function ImageLightbox({ images, startIndex, loadBlob, onClose }) {
  const [index, setIndex] = useState(startIndex);
  const [src, setSrc] = useState(null);
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);
  const cacheRef = useRef({}); // attId -> object URL

  const current = images[index];
  const multiple = images.length > 1;

  const clampScale = (s) => Math.min(5, Math.max(1, s));
  const resetZoom = useCallback(() => { setScale(1); setPos({ x: 0, y: 0 }); }, []);
  const zoomIn = () => setScale((s) => clampScale(s + 0.5));
  const zoomOut = () => setScale((s) => { const n = clampScale(s - 0.5); if (n === 1) setPos({ x: 0, y: 0 }); return n; });

  const go = useCallback((dir) => {
    const n = images.length;
    setIndex((i) => (((i + dir) % n) + n) % n);
    resetZoom();
  }, [images.length, resetZoom]);

  // Load the current image blob (cached per attachment).
  useEffect(() => {
    let active = true;
    if (!current) return;
    if (cacheRef.current[current.id]) { setSrc(cacheRef.current[current.id]); return; }
    setSrc(null);
    loadBlob(current.id).then((blob) => {
      if (!active || !blob) return;
      const u = URL.createObjectURL(blob);
      cacheRef.current[current.id] = u;
      setSrc(u);
    }).catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id]);

  useEffect(() => {
    const cache = cacheRef.current;
    return () => { Object.values(cache).forEach((u) => URL.revokeObjectURL(u)); };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight" && multiple) go(1);
      else if (e.key === "ArrowLeft" && multiple) go(-1);
      else if (e.key === "+" || e.key === "=") zoomIn();
      else if (e.key === "-") zoomOut();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, go, multiple]);

  const onWheel = (e) => {
    e.preventDefault();
    setScale((s) => { const n = clampScale(s + (e.deltaY < 0 ? 0.3 : -0.3)); if (n === 1) setPos({ x: 0, y: 0 }); return n; });
  };
  const onPointerDown = (e) => {
    if (scale === 1) return;
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    setPos({ x: dragRef.current.origX + (e.clientX - dragRef.current.startX), y: dragRef.current.origY + (e.clientY - dragRef.current.startY) });
  };
  const onPointerUp = () => { dragRef.current = null; };

  const download = () => {
    if (!src) return;
    const a = document.createElement("a");
    a.href = src; a.download = current?.original_filename || "image";
    document.body.appendChild(a); a.click(); a.remove();
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex flex-col bg-black/90 backdrop-blur-sm" data-testid="attachment-lightbox" onClick={onClose}>
      <div className="flex items-center justify-between px-4 py-3 text-white/90" onClick={(e) => e.stopPropagation()}>
        <span className="text-sm font-medium truncate max-w-[40%] flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}>
          {current?.original_filename}
          {multiple && <span className="text-white/50 text-xs tabular-nums" data-testid="lightbox-counter">{index + 1} / {images.length}</span>}
        </span>
        <div className="flex items-center gap-1">
          <button onClick={zoomOut} disabled={scale <= 1} className="p-2 rounded-lg hover:bg-white/10 disabled:opacity-30 transition-colors" title="Zoom out" data-testid="lightbox-zoom-out"><MagnifyingGlassMinus weight="bold" className="w-5 h-5" /></button>
          <span className="text-xs tabular-nums w-10 text-center select-none">{Math.round(scale * 100)}%</span>
          <button onClick={zoomIn} disabled={scale >= 5} className="p-2 rounded-lg hover:bg-white/10 disabled:opacity-30 transition-colors" title="Zoom in" data-testid="lightbox-zoom-in"><MagnifyingGlassPlus weight="bold" className="w-5 h-5" /></button>
          <button onClick={resetZoom} disabled={scale === 1 && pos.x === 0 && pos.y === 0} className="p-2 rounded-lg hover:bg-white/10 disabled:opacity-30 transition-colors" title="Reset" data-testid="lightbox-reset"><ArrowCounterClockwise weight="bold" className="w-5 h-5" /></button>
          <button onClick={download} className="p-2 rounded-lg hover:bg-white/10 transition-colors" title="Download" data-testid="lightbox-download"><DownloadSimple weight="bold" className="w-5 h-5" /></button>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 transition-colors ml-1" title="Close" data-testid="lightbox-close"><X weight="bold" className="w-5 h-5" /></button>
        </div>
      </div>
      <div className="flex-1 overflow-hidden flex items-center justify-center select-none relative" onWheel={onWheel} onClick={(e) => e.stopPropagation()}>
        {multiple && (
          <button onClick={() => go(-1)} className="absolute left-3 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors" title="Previous" data-testid="lightbox-prev"><CaretLeft weight="bold" className="w-6 h-6" /></button>
        )}
        {src ? (
          <img
            src={src}
            alt={current?.original_filename}
            draggable={false}
            onDoubleClick={() => (scale === 1 ? setScale(2) : resetZoom())}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            data-testid="lightbox-image"
            className="max-w-[88vw] max-h-[80vh] object-contain shadow-2xl"
            style={{
              transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
              cursor: scale > 1 ? (dragRef.current ? "grabbing" : "grab") : "zoom-in",
              transition: dragRef.current ? "none" : "transform 0.12s ease-out",
            }}
          />
        ) : (
          <div className="w-16 h-16 border-4 border-white/20 border-t-white/80 rounded-full animate-spin" />
        )}
        {multiple && (
          <button onClick={() => go(1)} className="absolute right-3 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors" title="Next" data-testid="lightbox-next"><CaretRight weight="bold" className="w-6 h-6" /></button>
        )}
      </div>
    </div>,
    document.body
  );
}

/**
 * Renders a single message attachment. Image types show an inline thumbnail
 * (authenticated blob) that opens the conversation gallery lightbox on click;
 * non-image types show a filename chip that opens in a new tab. Optional delete
 * (X) when `canDelete`.
 */
export function MessageAttachmentView({ attachment, loadBlob, onDelete, canDelete = false, testidPrefix = "attachment" }) {
  const [thumbUrl, setThumbUrl] = useState(null);
  const [soloOpen, setSoloOpen] = useState(false);
  const urlRef = useRef(null);
  const gallery = useContext(GalleryContext);
  const image = isImage(attachment.content_type);
  const attId = attachment.id;

  useEffect(() => {
    let active = true;
    if (image) {
      loadBlob(attId).then((blob) => {
        if (!active || !blob) return;
        const u = URL.createObjectURL(blob);
        urlRef.current = u;
        setThumbUrl(u);
      }).catch(() => {});
    }
    return () => {
      active = false;
      if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attId]);

  const openImage = () => {
    if (gallery) gallery.open(attId);
    else setSoloOpen(true);
  };

  const openInNewTab = useCallback(async () => {
    try {
      let u = urlRef.current;
      if (!u) { const blob = await loadBlob(attId); if (!blob) return; u = URL.createObjectURL(blob); }
      window.open(u, "_blank", "noopener");
      setTimeout(() => { if (!urlRef.current) URL.revokeObjectURL(u); }, 60000);
    } catch { /* no-op */ }
  }, [attId, loadBlob]);

  if (image) {
    return (
      <>
        <div className="relative inline-block" data-testid={`${testidPrefix}-${attId}`}>
          {thumbUrl ? (
            <img
              src={thumbUrl}
              alt={attachment.original_filename}
              onClick={openImage}
              className="max-w-[220px] max-h-[220px] rounded-lg border border-slate-200 cursor-zoom-in object-cover hover:opacity-90 transition-opacity"
              data-testid={`${testidPrefix}-open-${attId}`}
              title={`View ${attachment.original_filename}`}
            />
          ) : (
            <div className="w-[180px] h-[110px] rounded-lg border border-slate-200 bg-slate-100 animate-pulse" data-testid={`${testidPrefix}-loading-${attId}`} />
          )}
          {canDelete && (
            <button type="button" onClick={() => onDelete(attId)} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border border-slate-300 flex items-center justify-center text-slate-500 hover:text-rose-600 shadow" data-testid={`${testidPrefix}-delete-${attId}`} title="Delete attachment">
              <X weight="bold" className="w-3 h-3" />
            </button>
          )}
        </div>
        {soloOpen && thumbUrl && (
          <ImageLightbox images={[attachment]} startIndex={0} loadBlob={loadBlob} onClose={() => setSoloOpen(false)} />
        )}
      </>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 text-xs bg-white border border-slate-200 rounded-full pl-2.5 pr-1.5 py-1 text-slate-700 shadow-sm" data-testid={`${testidPrefix}-${attId}`}>
      <Paperclip weight="bold" className="w-3 h-3 text-slate-400 shrink-0" />
      <button type="button" onClick={openInNewTab} className="max-w-[200px] truncate text-blue-600 hover:underline" data-testid={`${testidPrefix}-open-${attId}`} title={`Open ${attachment.original_filename}`}>
        {attachment.original_filename}
      </button>
      {canDelete && (
        <button type="button" onClick={() => onDelete(attId)} className="text-slate-300 hover:text-rose-600 shrink-0" data-testid={`${testidPrefix}-delete-${attId}`} title="Delete attachment">
          <X weight="bold" className="w-3 h-3" />
        </button>
      )}
    </span>
  );
}
